import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Chip,
  Divider,
  Grid,
} from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import dayjs from "dayjs";

const ORDER_STATUS_MAP: Record<
  string,
  {
    label: string;
    color:
      | "default"
      | "primary"
      | "secondary"
      | "error"
      | "info"
      | "success"
      | "warning";
  }
> = {
  ORDERED: { label: "Pedido", color: "default" as const },
  PAID: { label: "Pago", color: "success" as const },
  CANCELED: { label: "Cancelado", color: "error" as const },
  FINISHED: { label: "Finalizado", color: "primary" as const },
};

interface ItemProduct {
  id: number;
  name: string;
  image: string | null;
  category: {
    id: number;
    name: string;
  } | null;
  producer: {
    id: number;
    name: string;
  };
}

interface Item {
  id: number;
  amount: number;
  price: string;
  total: string;
  product: ItemProduct;
}

export interface Order {
  address: string;
  created_at: string;
  date: string;
  delivery_date: string;
  delivery_fee: string;
  discount: string;
  id: number;
  items: Item[];
  items_count: number;
  status: "ORDERED" | "PAID" | "CANCELED" | "FINISHED";
  subtotal: number;
  total: number;
}

interface OrderDetailsModalProps {
  open: boolean;
  onClose: () => void;
  order: Order | null;
}

const formatCurrency = (value: string | number): string => {
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  return `R$ ${numValue.toFixed(2).replace(".", ",")}`;
};

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  open,
  onClose,
  order,
}) => {
  if (!order) {
    return null;
  }

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxWidth: "90vw",
          margin: "auto",
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
        <ReceiptIcon sx={{ mr: 1, color: "secondary.main" }} />
        <Typography component="span" variant="h6" color="secondary.main">
          Detalhes do Pedido #{order.id}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ overflowX: "hidden", px: 3 }}>
        <>
          {/* Informações Gerais */}
          <Grid
            container
            spacing={{ xs: 0, md: 3 }}
            sx={{
              mb: 0,
              "& > .MuiGrid-item": {
                paddingBottom: { xs: 3, md: 0 },
              },
            }}
          >
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{ p: 2, pb: 0, borderRadius: 2, height: "100%" }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="primary.main"
                  sx={{ display: "flex", alignItems: "center", mb: 2 }}
                >
                  <LocationOnIcon sx={{ mr: 1 }} /> Informações do Pedido
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Endereço:
                  </Typography>
                  <Typography variant="body1">{order.address}</Typography>
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Data do Pedido:
                  </Typography>
                  <Typography variant="body1">
                    {dayjs(order.created_at).format("DD/MM/YYYY HH:mm")}
                  </Typography>
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Data de Entrega:
                  </Typography>
                  <Typography variant="body1">
                    {dayjs(order.date).format("DD/MM/YYYY")}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Status:
                  </Typography>
                  <Chip
                    label={
                      ORDER_STATUS_MAP[order.status]?.label || order.status
                    }
                    color={ORDER_STATUS_MAP[order.status]?.color || "default"}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6} sx={{ mt: { xs: 4, md: 0 } }}>
              <Paper
                variant="outlined"
                sx={{ p: 2, pb: 0, borderRadius: 2, height: "100%" }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="primary.main"
                  sx={{ display: "flex", alignItems: "center", mb: 2 }}
                >
                  <ShoppingCartIcon sx={{ mr: 1 }} /> Resumo Financeiro
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Subtotal:
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatCurrency(order.subtotal)}
                  </Typography>
                </Box>
                {parseFloat(order.delivery_fee) > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Taxa de Entrega:
                    </Typography>
                    <Typography variant="body1">
                      {formatCurrency(order.delivery_fee)}
                    </Typography>
                  </Box>
                )}
                {parseFloat(order.discount) > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Desconto:
                    </Typography>
                    <Typography variant="body1" color="success.main">
                      -{formatCurrency(order.discount)}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total:
                  </Typography>
                  <Typography
                    variant="h6"
                    color="primary.main"
                    fontWeight={700}
                  >
                    {formatCurrency(order.total)}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Tabela de Itens */}
          <Typography
            variant="subtitle1"
            fontWeight={600}
            color="primary.main"
            sx={{ mb: 2, mt: 6 }}
          >
            Itens do Pedido ({order.items_count || 0}{" "}
            {(order.items_count || 0) === 1 ? "item" : "itens"})
          </Typography>
          {order.items && order.items.length > 0 ? (
            <>
              {/* Desktop: Tabela */}
              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{
                    width: "100%",
                    overflowX: "auto",
                    maxWidth: "100%",
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Produto</TableCell>
                        <TableCell>Produtor</TableCell>
                        <TableCell align="center">Qtd.</TableCell>
                        <TableCell align="right">Preço</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {order.items?.map((item: Item) => {
                        const unitPrice = item.price
                          ? parseFloat(item.price)
                          : 0;
                        const totalPrice = unitPrice * (item.amount || 0);

                        return (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {item.product.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {item.product.producer.name}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" fontWeight={500}>
                                {item.amount || 0}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">
                                {item.price ? formatCurrency(item.price) : "-"}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={500}>
                                {formatCurrency(totalPrice)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Mobile: Cards */}
              <Box sx={{ display: { xs: "block", md: "none" } }}>
                <Grid container spacing={2}>
                  {order.items?.map((item) => {
                    const unitPrice = item.price ? parseFloat(item.price) : 0;
                    const totalPrice = unitPrice * (item.amount || 0);

                    return (
                      <Grid item xs={12} key={item.id}>
                        <Paper
                          variant="outlined"
                          sx={{ p: 2, borderRadius: 2 }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            color="primary.main"
                            sx={{ mb: 1 }}
                          >
                            {item.product.name}
                          </Typography>

                          <Grid container spacing={1} sx={{ mb: 1 }}>
                            <Grid item xs={6}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Produtor:
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.85rem" }}
                              >
                                {item.product.producer.name}
                              </Typography>
                            </Grid>
                          </Grid>

                          <Grid container spacing={1}>
                            <Grid item xs={4}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Quantidade:
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {item.amount || 0}
                              </Typography>
                            </Grid>

                            <Grid item xs={4}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Preço Unit.:
                              </Typography>
                              <Typography variant="body2">
                                {item.price ? formatCurrency(item.price) : "-"}
                              </Typography>
                            </Grid>

                            <Grid item xs={4}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Total:
                              </Typography>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color="primary.main"
                              >
                                {formatCurrency(totalPrice)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: "center", p: 4 }}>
              <Typography variant="body1" color="text.secondary">
                Nenhum item encontrado neste pedido.
              </Typography>
            </Box>
          )}
        </>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button onClick={handleClose} variant="outlined" size="large">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderDetailsModal;
